import React, { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatGBP } from "@/lib/format";
import PatternOverlay from "@/components/PatternOverlay";

// Palette pillars — mirrors backend PALETTE_NAMES
const PALETTE_ORDER = ["Blæk", "Vétiver", "Ivoire"];
const DUO_RATE = 0.15;
const TRIO_RATE = 0.22;

const PAIRING_STORY = {
  "Blæk|Vétiver": {
    name: "Nocturne",
    desc: "Bright grasses ignite over smoked oud — sunrise crossing a still-warm midnight.",
  },
  "Blæk|Ivoire": {
    name: "Eclipse",
    desc: "Powdered iris slipped over dark resin — the hush after a long, close conversation.",
  },
  "Ivoire|Vétiver": {
    name: "Aurora",
    desc: "Citrus air meeting warm skin musk — the moment light first colours the room.",
  },
};

const TRIO_STORY = {
  name: "The Trilogy",
  desc: "All three pillars worn as one — dawn, noon and midnight braided into a single trail. The full signature.",
};

const pairingKey = (a, b) => [a, b].sort().join("|");

export default function LayerYourSignature({ initialSelected }) {
  const [palette, setPalette] = useState([]);
  const [selected, setSelected] = useState(() => new Set(initialSelected || []));
  const { add, setOpen } = useCart();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        const map = new Map(data.map((p) => [p.name, p]));
        const ordered = PALETTE_ORDER.map((n) => map.get(n)).filter(Boolean);
        setPalette(ordered);
      } catch {}
    })();
  }, []);

  const toggle = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        if (next.size >= 3) {
          // rotate — drop oldest
          const first = next.values().next().value;
          next.delete(first);
        }
        next.add(name);
      }
      return next;
    });
  };

  const selectedProducts = useMemo(
    () => palette.filter((p) => selected.has(p.name)),
    [palette, selected]
  );

  const listPrice = selectedProducts.reduce((s, p) => s + (p.price || 0), 0);
  const isDuo = selectedProducts.length === 2;
  const isTrio = selectedProducts.length === 3;
  const activeRate = isTrio ? TRIO_RATE : isDuo ? DUO_RATE : 0;
  const savings = Math.round(listPrice * activeRate * 100) / 100;
  const bundlePrice = listPrice - savings;

  const story = isTrio
    ? TRIO_STORY
    : isDuo
    ? PAIRING_STORY[pairingKey(selectedProducts[0].name, selectedProducts[1].name)]
    : null;

  // Precompute full-trio "compose the trio" total for the affordance
  const trioListPrice = palette.reduce((s, p) => s + (p.price || 0), 0);
  const trioSavings = Math.round(trioListPrice * TRIO_RATE * 100) / 100;
  const trioBundlePrice = trioListPrice - trioSavings;

  const handleAddDuo = () => {
    selectedProducts.forEach((p) => add(p, 1));
    setOpen(true);
  };

  const handleAddTrio = () => {
    setSelected(new Set(palette.map((p) => p.name)));
    palette.forEach((p) => add(p, 1));
    setOpen(true);
  };

  if (palette.length < 2) return null;

  return (
    <section
      className="pattern-bg py-20 md:py-24 border-t border-border relative"
      data-testid="layer-your-signature"
    >
      <PatternOverlay opacity={0.09} size={480} />
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-[0.32em] text-primary">
            Layer Your Signature
          </div>
          <h3 className="font-serif text-4xl md:text-5xl mt-4 tracking-tight leading-[1.05]">
            Two scents. One accord.
          </h3>
          <p className="mt-5 text-muted-foreground leading-relaxed">
            The palette is designed to combine. Choose any two pillars for a duo
            at <span className="text-primary">15% off</span> — or wear all three
            as a trilogy at <span className="text-primary">22% off</span>.
          </p>
        </div>

        {/* Palette tiles */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {palette.map((p) => {
            const active = selected.has(p.name);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.name)}
                data-testid={`palette-tile-${p.name.toLowerCase()}`}
                className={`group relative text-left border transition-all overflow-hidden ${
                  active
                    ? "border-primary ring-1 ring-primary/60"
                    : "border-border hover:border-primary/50"
                }`}
                aria-pressed={active}
              >
                <div className="aspect-[4/5] bg-secondary overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="absolute top-3 right-3">
                  <div
                    className={`h-7 w-7 rounded-full grid place-items-center border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background/80 border-border text-muted-foreground"
                    }`}
                  >
                    {active ? <Check size={14} /> : null}
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-serif text-3xl text-primary">
                    {p.name}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-[0.24em] text-muted-foreground">
                    {p.concentration}
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground">
                    {formatGBP(p.price)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Bundle summary */}
        <div
          className="mt-10 border border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:justify-between bg-background/40 backdrop-blur-sm"
          data-testid="duo-summary"
        >
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">
              {isTrio
                ? "Your Trilogy"
                : isDuo
                ? "Your Duo"
                : "Pick two to compose your duo"}
            </div>
            {isDuo || isTrio ? (
              <>
                <div className="mt-2 font-serif text-2xl md:text-3xl">
                  {selectedProducts.map((p) => p.name).join(" + ")}
                  {story ? (
                    <span className="text-muted-foreground text-base italic ml-3">
                      — {story.name}
                    </span>
                  ) : null}
                </div>
                {story ? (
                  <p className="mt-2 text-sm text-muted-foreground max-w-xl">
                    {story.desc}
                  </p>
                ) : null}
              </>
            ) : (
              <div className="mt-2 text-sm text-muted-foreground">
                {selectedProducts.length === 1
                  ? `${selectedProducts[0].name} selected — add one more to unlock the duo price.`
                  : "Tap two pillars above to see the pairing."}
              </div>
            )}
          </div>

          <div className="flex items-center gap-6 md:gap-8 shrink-0">
            {(isDuo || isTrio) ? (
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
                  {isTrio ? "As a trilogy" : "As a duo"}
                </div>
                <div className="font-serif text-3xl text-primary" data-testid="duo-price">
                  {formatGBP(bundlePrice)}
                </div>
                <div className="text-xs text-muted-foreground line-through">
                  {formatGBP(listPrice)}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-primary mt-1">
                  Save {formatGBP(savings)}
                </div>
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleAddDuo}
              disabled={!isDuo && !isTrio}
              data-testid="add-duo-btn"
              className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-8 py-4 hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isTrio ? "Add Trilogy to Cart" : "Add Duo to Cart"}
            </button>
          </div>
        </div>

        {/* Trio upsell — subtle second tier */}
        {!isTrio && palette.length === 3 && (
          <div
            className="mt-4 border border-dashed border-primary/40 px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            data-testid="trio-upsell"
          >
            <div className="text-sm text-muted-foreground">
              <span className="uppercase tracking-[0.24em] text-[10px] text-primary mr-3">
                Or go further
              </span>
              Compose the trio —{" "}
              <span className="text-foreground">
                {formatGBP(trioBundlePrice)}
              </span>{" "}
              <span className="line-through opacity-60 ml-1">
                {formatGBP(trioListPrice)}
              </span>
              <span className="text-primary ml-2">save {formatGBP(trioSavings)}</span>
            </div>
            <button
              type="button"
              onClick={handleAddTrio}
              data-testid="add-trio-btn"
              className="border border-primary/70 text-primary uppercase tracking-[0.22em] text-[11px] px-6 py-2.5 hover:bg-primary hover:text-primary-foreground transition-colors"
            >
              Compose the Trio
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
